import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import Logo from './Logo';
import { RECURSOS } from '../data/recursos';

const Layout = () => {
  const { t, i18n } = useTranslation();
  const { currentUser, logout } = useAuth();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showResources, setShowResources] = useState(false);
  const userMenuRef = useRef(null);
  const resourcesRef = useRef(null);

  // Tanca els menús en clicar a fora. Van tots dos al mateix lloc perquè
  // aquest efecte ja hi era per al menú d'usuari i dos escoltadors sobre
  // `mousedown` per fer la mateixa feina només serveix per oblidar-se'n un.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (resourcesRef.current && !resourcesRef.current.contains(event.target)) {
        setShowResources(false);
      }
    };

    // Un menú obert s'ha de poder tancar sense el ratolí.
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setShowUserMenu(false);
        setShowResources(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  // La descripció de cada recurs viu al costat del recurs, no a les
  // traduccions; si un dia n'hi falta la de l'idioma actiu, es cau al català,
  // que és l'idioma per defecte de l'aplicació.
  const descripcioRecurs = (recurs) =>
    recurs.descripcio?.[i18n.language?.split('-')[0]] || recurs.descripcio?.ca || '';

  return (
    <div className="h-screen bg-neutral-50 flex flex-col">
      {/* Navigation */}
      <nav className="bg-white border-b border-neutral-200 shadow-sm">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center">
              {/* La marca substitueix el subtítol que hi havia sota el nom: un
                  símbol ocupa menys i deixa l'espai a la navegació, que és el
                  que la gent hi busca de debò. */}
              <Link to="/dashboard" className="flex flex-shrink-0 items-center gap-2.5" title="Lucifer">
                <Logo size={22} className="text-accent-700" />
                <h1 className="text-base font-bold tracking-[0.16em] text-neutral-900">Lucifer</h1>
              </Link>
              <div className="hidden md:block">
                <div className="ml-8 flex items-center space-x-0.5">
                  <Link
                    to="/dashboard"
                    className={`flex items-center px-2 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      location.pathname.startsWith('/dashboard') 
                        ? 'bg-accent-50 text-accent-700 shadow-sm' 
                        : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50'
                    }`}
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                    {t('navigation.dashboard')}
                  </Link>
                  <Link
                    to="/workspaces"
                    className={`flex items-center px-2 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      location.pathname.startsWith('/workspaces') || location.pathname.startsWith('/projects')
                        ? 'bg-accent-50 text-accent-700 shadow-sm' 
                        : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50'
                    }`}
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    {t('navigation.workspaces')}
                  </Link>
                  {currentUser?.role === 'administrator' && (
                    <>
                      <Link
                        to="/user-management"
                        className={`flex items-center px-2 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                          location.pathname.startsWith('/user-management')
                            ? 'bg-accent-50 text-accent-700 shadow-sm' 
                            : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50'
                        }`}
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        {t('userManagement.title')}
                      </Link>
                      <Link
                        to="/admin-settings"
                        className={`flex items-center px-2 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                          location.pathname.startsWith('/admin-settings')
                            ? 'bg-accent-50 text-accent-700 shadow-sm' 
                            : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50'
                        }`}
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {t('adminSettings.title')}
                      </Link>
                    </>
                  )}

                  {/* Recursos — enllaços de fora de l'aplicació.
                      No és una ruta: no es marca mai com a activa i cada
                      entrada obre una pestanya nova. La llista és a
                      src/data/recursos.js. */}
                  <div className="relative" ref={resourcesRef}>
                    <button
                      type="button"
                      onClick={() => setShowResources(!showResources)}
                      aria-haspopup="menu"
                      aria-expanded={showResources}
                      className={`flex items-center px-2 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                        showResources
                          ? 'bg-neutral-100 text-neutral-900'
                          : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50'
                      }`}
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                      {t('navigation.resources')}
                      <svg className={`ml-1.5 h-3.5 w-3.5 text-neutral-400 transition-transform ${showResources ? 'rotate-180' : ''}`}
                           fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {showResources && (
                      <div role="menu"
                           className="absolute left-0 mt-2 w-80 rounded-lg border border-neutral-200 bg-white py-1 shadow-lg z-20">
                        {RECURSOS.map(recurs => (
                          <a
                            key={recurs.id}
                            href={recurs.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            role="menuitem"
                            onClick={() => setShowResources(false)}
                            className="block px-4 py-3 transition-colors hover:bg-neutral-50"
                          >
                            <span className="flex items-center gap-1.5 text-sm font-medium text-neutral-900">
                              {recurs.nom}
                              <svg className="h-3.5 w-3.5 flex-shrink-0 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </span>
                            <span className="mt-0.5 block text-xs leading-relaxed text-neutral-500">
                              {descripcioRecurs(recurs)}
                            </span>
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              
              {/* User Menu */}
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-3 px-3 py-2 rounded-lg text-sm hover:bg-neutral-50 transition-colors group"
                >
                  <div className="w-8 h-8 bg-gradient-to-br from-neutral-100 to-neutral-200 rounded-full flex items-center justify-center group-hover:from-accent-100 group-hover:to-accent-200 transition-all">
                    <span className="text-neutral-600 group-hover:text-accent-700 font-medium text-sm">
                      {(currentUser?.full_name || currentUser?.username)?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                  <div className="hidden sm:block text-left">
                    <div className="text-neutral-900 font-medium">
                      {currentUser?.full_name || currentUser?.username}
                    </div>
                    <div className="text-xs text-neutral-500">
                      {t(`roles.${currentUser?.role}`)}
                    </div>
                  </div>
                  <svg className="w-4 h-4 text-neutral-400 group-hover:text-neutral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown Menu */}
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-neutral-200 py-2 z-20">
                    <div className="px-4 py-3 border-b border-neutral-100">
                      <div className="text-sm font-medium text-neutral-900">
                        {currentUser?.full_name || currentUser?.username}
                      </div>
                      <div className="text-xs text-neutral-500 mt-1">
                        {currentUser?.email} • {t(`roles.${currentUser?.role}`)}
                      </div>
                    </div>
                    
                    <div className="py-1">
                      <Link
                        to="/profile"
                        className="flex items-center px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <svg className="mr-3 h-4 w-4 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        {t('layout.myProfile')}
                      </Link>
                      
                    </div>
                    
                    <div className="border-t border-neutral-100 pt-1">
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          logout();
                        }}
                        className="flex items-center w-full px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
                      >
                        <svg className="mr-3 h-4 w-4 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        {t('auth.logout')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <div className="mx-auto max-w-7xl py-6 sm:px-6 lg:px-8">
          <Outlet />
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-neutral-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex flex-col sm:flex-row justify-between items-center text-xs text-neutral-500">
            <div className="mb-1 sm:mb-0">
              © {new Date().getFullYear()} Lucifer. {t('layout.allRightsReserved')}.
            </div>
            <div>
              {t('layout.aProjectOf')}{' '}
              <a
                href="https://strategos.cat"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-neutral-700 underline decoration-neutral-300 underline-offset-2 hover:text-accent-700"
              >
                Strategos.cat
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;